// Case Buddy addendum/path-ending review methods.
// These are Options API method definitions and intentionally use `this`.

import {
  buildCaseAddressLegendAnalysis,
  buildCaseAddendumAuditRows,
  buildCaseAddendumExchangeKeywordList,
  buildCaseAddendumPathEndingReviewAuditRows,
  buildCaseAddendumPathEndingReviewOutputRows,
  buildCaseAddendumPathEndingReviewState,
  buildCaseAddendumPreviewRows,
  buildCaseAddendumRecipientEntityAuditRows,
  buildCaseAddendumRecipientEntityOutputRows,
  buildCaseAddendumRecipientEntityReviewState,
  buildCaseAddendumState,
  buildTsv,
  CASE_ADDENDUM_BUILD_REVIEW_ID,
  CASE_ADDENDUM_RENDERER_VERSION,
  CASE_ADDENDUM_VARIANT_NARRATIVE,
  CASE_ADDENDUM_VARIANT_STRUCTURED,
  downloadTextFile,
  getCaseAddendumVariantPayload as buildCaseAddendumVariantPayload,
  normalizeCell,
  WORKFLOW_SIGNAL_ADDENDUM_RECIPIENTS_RESOLVED,
  WORKFLOW_SIGNAL_ADDENDUM_REVIEWED,
  WORKFLOW_SIGNAL_PATH_ENDING_ROWS_REVIEWED
} from '../CaseBuddyCore.js';

export const caseBuddyAddendumMethods = {
    getAddendumPathEndingReviewState(options = {}) {
      const whatWentWhereState = options.whatWentWhereState || this.getWhatWentWhereState(options);
      const buildOptions = {
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        dedupe: this.addendumPathEndingDedupe !== false,
        requirePathEnds: this.addendumPathEndingRequirePathEnds !== false,
        prerequisiteCanBuild: !!whatWentWhereState.canBuild,
        prerequisiteReviewIsCurrent: !!whatWentWhereState.reviewIsCurrent,
        prerequisiteSummary: whatWentWhereState.summary || '',
        whatWentWhereBasisHash: whatWentWhereState.currentBasisHash || '',
        ...(options || {}),
        whatWentWhereState: undefined
      };
      const cacheKey = this.getCaseComputationCacheKey?.('addendum-path-ending-state', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => {
        const keywords = buildCaseAddendumExchangeKeywordList(this.depositHotWalletEffectiveCurator || {});
        return buildCaseAddendumPathEndingReviewState(
          whatWentWhereState.outputRows || [],
          keywords,
          this.caseSupportState.workflowState || {},
          buildOptions
        );
      }) || (() => {
        const keywords = buildCaseAddendumExchangeKeywordList(this.depositHotWalletEffectiveCurator || {});
        return buildCaseAddendumPathEndingReviewState(whatWentWhereState.outputRows || [], keywords, this.caseSupportState.workflowState || {}, buildOptions);
      })();
    },

    async openAddendumPathEndingReviewModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening addendum path-ending review…',
        message: 'Filtering exchange path-ending rows for the addendum.',
        target: 'addendum-path-ending-modal',
        finishMessage: 'Addendum path-ending review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.showAddendumRecipientEntityModal = false;
        this.showCaseAddendumModal = false;
        this.addendumPathEndingSearch = '';
        this.addendumPathEndingViewFilter = options.view || 'included';
        this.showAddendumPathEndingReviewModal = true;
        await this.yieldToCaseUi();
        await this.refreshAddendumPathEndingReview({ silent: true, forceRemote: !!options.forceRemote });
        return true;
      });
    },

    cancelAddendumPathEndingReviewModal() {
      if (this.addendumPathEndingRefreshRunning) return;
      this.showAddendumPathEndingReviewModal = false;
    },

    async refreshAddendumPathEndingReview(options = {}) {
      if (this.addendumPathEndingRefreshRunning) return this.getAddendumPathEndingReviewState();
      this.addendumPathEndingRefreshRunning = true;
      try {
        if (options.forceRemote) {
          await this.loadEntityCuratorRemoteKeywords({ force: true, silent: true });
        }
        const state = this.getAddendumPathEndingReviewState();
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          addendum_path_endings: this.buildAddendumPathEndingReviewCheckResult(state)
        };
        this.persistCaseSupportState();
        this.addendumPathEndingStatus = state.summary || 'Addendum path-ending review refreshed.';
        if (!options.silent) this.showCaseToast(this.addendumPathEndingStatus);
        return state;
      } finally {
        this.addendumPathEndingRefreshRunning = false;
      }
    },

    buildAddendumPathEndingReviewCheckResult(state = null) {
      const reviewState = state || this.getAddendumPathEndingReviewState();
      if (!reviewState.prerequisiteCanBuild) {
        return this.buildCheckResult('addendum_path_endings', 'fail', reviewState.summary || 'Build What Went Where before reviewing addendum path-ending rows.', reviewState);
      }
      if ((reviewState.keywordCount || 0) === 0) {
        return this.buildCheckResult('addendum_path_endings', 'fail', reviewState.summary || 'No exchange keywords are loaded for addendum path-ending review.', reviewState);
      }
      if (reviewState.reviewIsCurrent) {
        return this.buildCheckResult('addendum_path_endings', 'pass', `Addendum path-ending review is current. ${reviewState.includedCount || 0} row${(reviewState.includedCount || 0) === 1 ? '' : 's'} included; ${reviewState.omittedCount || 0} omitted due to exclusion criteria.`, reviewState);
      }
      return this.buildCheckResult('addendum_path_endings', 'warn', `${reviewState.includedCount || 0} exchange path-ending row${(reviewState.includedCount || 0) === 1 ? '' : 's'} are ready for addendum review; ${reviewState.omittedCount || 0} row${(reviewState.omittedCount || 0) === 1 ? '' : 's'} omitted due to exclusion criteria. Mark reviewed when the filter output looks right.`, reviewState);
    },

    updateAddendumPathEndingReviewCheckResult(state = null) {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        addendum_path_endings: this.buildAddendumPathEndingReviewCheckResult(state)
      };
      this.persistCaseSupportState();
    },

    stageAddendumPathEndingReviewRows(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging addendum path endings…',
          message: 'Building addendum review rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Addendum path endings staged.'
        }, () => this.stageAddendumPathEndingReviewRows({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getAddendumPathEndingReviewState();
      if (!state.prerequisiteCanBuild) {
        this.addendumPathEndingStatus = state.summary || 'Build What Went Where before staging addendum path-ending rows.';
        if (!options.silent) this.showCaseToast(this.addendumPathEndingStatus);
        return false;
      }
      if ((state.includedCount || 0) === 0) {
        this.addendumPathEndingStatus = 'No exchange path-ending rows are included by the current filter.';
        if (!options.silent) this.showCaseToast(this.addendumPathEndingStatus);
        return false;
      }
      this.loadOutputRows(buildCaseAddendumPathEndingReviewOutputRows(state), {
        title: 'Addendum exchange path-ending rows',
        detail: `Tool55-style filter staged ${state.includedCount || 0} exchange path-ending row${(state.includedCount || 0) === 1 ? '' : 's'} in Output. Main Data is not modified.`,
        sourceActionId: 'review_path_ending_rows',
        target: 'addendum-review-preview'
      });
      this.addendumPathEndingStatus = `Staged ${state.includedCount || 0} exchange path-ending row${(state.includedCount || 0) === 1 ? '' : 's'} in Output.`;
      if (!options.silent) this.showCaseToast(this.addendumPathEndingStatus);
      this.scrollToSection('case-output');
      return true;
    },

    stageAddendumPathEndingReviewAudit(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging addendum path-ending audit…',
          message: 'Building addendum review rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Addendum path-ending audit staged.'
        }, () => this.stageAddendumPathEndingReviewAudit({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getAddendumPathEndingReviewState();
      this.loadOutputRows(buildCaseAddendumPathEndingReviewAuditRows(state, options.actionLabel || 'Addendum path-ending review audit'), {
        title: 'Addendum path-ending review audit',
        detail: 'Audit-only review of included and omitted addendum path-ending rows. Main Data is not modified.',
        sourceActionId: 'review_path_ending_rows_audit',
        target: 'audit-only'
      });
      this.addendumPathEndingStatus = `Addendum path-ending audit staged. ${state.omittedCount || 0} row${(state.omittedCount || 0) === 1 ? '' : 's'} omitted due to exclusion criteria.`;
      if (!options.silent) this.showCaseToast(this.addendumPathEndingStatus);
      this.scrollToSection('case-output');
      return true;
    },

    downloadAddendumPathEndingReviewTsv(options = {}) {
      const state = this.getAddendumPathEndingReviewState();
      if (!state.prerequisiteCanBuild || (state.includedCount || 0) === 0) {
        this.addendumPathEndingStatus = state.summary || 'No addendum path-ending rows are ready to download.';
        if (!options.silent) this.showCaseToast(this.addendumPathEndingStatus);
        return false;
      }
      downloadTextFile('addendum_exchange_path_endings.tsv', buildTsv(buildCaseAddendumPathEndingReviewOutputRows(state)), 'text/tab-separated-values;charset=utf-8;');
      this.addendumPathEndingStatus = 'Addendum exchange path-ending TSV downloaded.';
      if (!options.silent) this.showCaseToast(this.addendumPathEndingStatus);
      return true;
    },

    markAddendumPathEndingReviewComplete(options = {}) {
      const state = this.getAddendumPathEndingReviewState();
      if (!state.prerequisiteCanBuild) {
        this.addendumPathEndingStatus = state.summary || 'Build What Went Where before marking addendum path-ending review complete.';
        this.showCaseToast(this.addendumPathEndingStatus);
        return false;
      }
      if ((state.keywordCount || 0) === 0) {
        this.addendumPathEndingStatus = state.summary || 'Exchange keyword list is empty.';
        this.showCaseToast(this.addendumPathEndingStatus);
        return false;
      }
      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.workflowState.addendumPathEndingReview = {
        basisHash: state.currentBasisHash,
        reviewedAtUtc,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        includedCount: state.includedCount || 0,
        omittedCount: state.omittedCount || 0,
        omittedReasonCounts: { ...(state.omittedReasonCounts || {}) },
        keywordCount: state.keywordCount || 0,
        requirePathEnds: !!state.requirePathEnds,
        dedupe: !!state.dedupe
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_PATH_ENDING_ROWS_REVIEWED, options.source || 'review_path_ending_rows', { silent: true });
      const refreshed = this.getAddendumPathEndingReviewState();
      this.updateAddendumPathEndingReviewCheckResult(refreshed);
      this.loadOutputRows(buildCaseAddendumPathEndingReviewAuditRows(refreshed, 'Review confirmed'), {
        title: 'Addendum path-ending review audit',
        detail: 'Addendum exchange path-ending rows were marked reviewed for the current case data.',
        sourceActionId: 'review_path_ending_rows',
        target: 'audit-only'
      });
      this.addendumPathEndingStatus = `Addendum path-ending review marked current. ${refreshed.includedCount || 0} row${(refreshed.includedCount || 0) === 1 ? '' : 's'} included; ${refreshed.omittedCount || 0} omitted due to exclusion criteria.`;
      this.showCaseToast(this.addendumPathEndingStatus);
      return true;
    },

    getAddendumRecipientEntityReviewState(options = {}) {
      const pathEndingState = options.pathEndingState || this.getAddendumPathEndingReviewState(options);
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        ...(options || {}),
        pathEndingState: undefined
      };
      const cacheKey = this.getCaseComputationCacheKey?.('addendum-recipient-state', {
        ...buildOptions,
        pathEndingBasis: pathEndingState?.currentBasisHash || '',
        pathEndingReviewed: !!pathEndingState?.reviewIsCurrent
      }) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseAddendumRecipientEntityReviewState(
        pathEndingState,
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseAddendumRecipientEntityReviewState(pathEndingState, this.caseSupportState.workflowState || {}, buildOptions);
    },

    initializeAddendumRecipientEntityDrafts() {
      const state = this.getAddendumRecipientEntityReviewState();
      const validKeys = new Set((state.candidates || []).map(candidate => candidate.reviewKey));
      const drafts = Object.fromEntries(
        Object.entries(this.addendumRecipientDraftByKey || {}).filter(([key]) => validKeys.has(key))
      );
      (state.candidates || []).forEach((candidate) => {
        if (drafts[candidate.reviewKey] == null) {
          drafts[candidate.reviewKey] = candidate.resolvedEntityName || '';
        }
      });
      this.addendumRecipientDraftByKey = drafts;
      return state;
    },

    async openAddendumRecipientEntityModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening addendum recipient review…',
        message: 'Resolving recipient entities for addendum rows.',
        target: 'addendum-recipient-modal',
        finishMessage: 'Addendum recipient review ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.showAddendumPathEndingReviewModal = false;
        this.showCaseAddendumModal = false;
        this.addendumRecipientSearch = '';
        this.addendumRecipientViewFilter = options.view || 'needs_choice';
        this.showAddendumRecipientEntityModal = true;
        await this.yieldToCaseUi();
        this.initializeAddendumRecipientEntityDrafts();
        await this.refreshAddendumRecipientEntityReview({ silent: true });
        return true;
      });
    },

    cancelAddendumRecipientEntityModal() {
      if (this.addendumRecipientRefreshRunning) return;
      this.showAddendumRecipientEntityModal = false;
    },

    refreshAddendumRecipientEntityReview(options = {}) {
      if (this.addendumRecipientRefreshRunning) return this.getAddendumRecipientEntityReviewState();
      this.addendumRecipientRefreshRunning = true;
      try {
        const state = this.initializeAddendumRecipientEntityDrafts();
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          addendum_recipient_entities: this.buildAddendumRecipientEntityCheckResult(state)
        };
        this.persistCaseSupportState();
        this.addendumRecipientStatus = state.summary || 'Addendum recipient entity review refreshed.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return state;
      } finally {
        this.addendumRecipientRefreshRunning = false;
      }
    },

    buildAddendumRecipientEntityCheckResult(state = null) {
      const reviewState = state || this.getAddendumRecipientEntityReviewState();
      if (!reviewState.buildIsCurrent) {
        return this.buildCheckResult('addendum_recipient_entities', 'fail', reviewState.summary || 'Review exchange path-ending addendum rows before resolving recipient entities.', reviewState);
      }
      if ((reviewState.noCandidateEntityCount || 0) > 0) {
        return this.buildCheckResult('addendum_recipient_entities', 'fail', `${reviewState.noCandidateEntityCount} addendum row${reviewState.noCandidateEntityCount === 1 ? '' : 's'} have no candidate entity options. Refresh/review the Tool55-style keyword filter before confirming recipients.`, reviewState);
      }
      if ((reviewState.needsChoiceCount || 0) > 0) {
        return this.buildCheckResult('addendum_recipient_entities', 'fail', `${reviewState.needsChoiceCount} addendum recipient row${reviewState.needsChoiceCount === 1 ? '' : 's'} still need an entity choice.`, reviewState);
      }
      if (reviewState.reviewIsCurrent) {
        return this.buildCheckResult('addendum_recipient_entities', 'pass', `Addendum recipient entities are current. ${reviewState.resolvedCount || 0}/${reviewState.totalCandidateCount || 0} row${(reviewState.totalCandidateCount || 0) === 1 ? '' : 's'} resolved.`, reviewState);
      }
      return this.buildCheckResult('addendum_recipient_entities', 'warn', `${reviewState.resolvedCount || 0}/${reviewState.totalCandidateCount || 0} addendum recipient row${(reviewState.totalCandidateCount || 0) === 1 ? '' : 's'} are resolved. Mark the review complete when the selected entities look right.`, reviewState);
    },

    updateAddendumRecipientEntityCheckResult(state = null) {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        addendum_recipient_entities: this.buildAddendumRecipientEntityCheckResult(state)
      };
      this.persistCaseSupportState();
    },

    getAddendumRecipientDraftValue(candidate = {}) {
      if (!candidate?.reviewKey) return '';
      return this.addendumRecipientDraftByKey?.[candidate.reviewKey] ?? candidate.resolvedEntityName ?? '';
    },

    setAddendumRecipientDraftValue(reviewKey = '', value = '') {
      if (!reviewKey) return;
      this.addendumRecipientDraftByKey = {
        ...(this.addendumRecipientDraftByKey || {}),
        [reviewKey]: value
      };
    },

    saveAddendumRecipientEntityChoice(candidate = null, selectedEntity = '', options = {}) {
      if (!candidate?.reviewKey) {
        this.addendumRecipientStatus = 'Select an addendum recipient row first.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return null;
      }
      if (candidate.isAutoResolved) {
        this.addendumRecipientStatus = 'This row is already auto-resolved because only one entity matched.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return this.refreshAddendumRecipientEntityReview({ silent: true });
      }
      const selection = normalizeCell(selectedEntity || this.getAddendumRecipientDraftValue(candidate));
      const match = (candidate.candidateEntityNames || []).find(option => normalizeCell(option).toLowerCase() === selection.toLowerCase());
      if (!match) {
        this.addendumRecipientStatus = 'Select one of the candidate entity options before saving.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return null;
      }

      const existingReview = this.caseSupportState.workflowState.addendumRecipientEntityReview && typeof this.caseSupportState.workflowState.addendumRecipientEntityReview === 'object'
        ? this.caseSupportState.workflowState.addendumRecipientEntityReview
        : {};
      const decisionsByReviewKey = { ...(existingReview.decisionsByReviewKey || existingReview.decisionsByKey || {}) };
      const now = new Date().toISOString();
      decisionsByReviewKey[candidate.reviewKey] = {
        reviewKey: candidate.reviewKey,
        status: 'selected',
        selectedEntity: match,
        rowId: candidate.rowId,
        sourceRowNumber: candidate.sourceRowNumber,
        pathLabel: candidate.effectivePathLabel,
        transactionHash: candidate.effectiveTransactionHash,
        toAddress: candidate.toAddress,
        reviewBasisHash: candidate.reviewBasisHash,
        reviewedAtUtc: now,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        source: options.source || 'addendum-recipient-modal'
      };
      this.caseSupportState.workflowState.addendumRecipientEntityReview = {
        ...existingReview,
        decisionsByReviewKey,
        lastTouchedUtc: now
      };
      this.addendumRecipientDraftByKey = {
        ...(this.addendumRecipientDraftByKey || {}),
        [candidate.reviewKey]: match
      };
      const refreshed = this.refreshAddendumRecipientEntityReview({ silent: true });
      this.addendumRecipientStatus = `Recipient entity saved for row ${candidate.sourceRowNumber || '—'}. ${refreshed.needsChoiceCount || 0} row${(refreshed.needsChoiceCount || 0) === 1 ? '' : 's'} still need a choice.`;
      if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
      return refreshed;
    },

    clearAddendumRecipientEntityChoice(candidate = null, options = {}) {
      if (!candidate?.reviewKey || candidate.isAutoResolved) return this.getAddendumRecipientEntityReviewState();
      const existingReview = this.caseSupportState.workflowState.addendumRecipientEntityReview && typeof this.caseSupportState.workflowState.addendumRecipientEntityReview === 'object'
        ? this.caseSupportState.workflowState.addendumRecipientEntityReview
        : {};
      const decisionsByReviewKey = { ...(existingReview.decisionsByReviewKey || existingReview.decisionsByKey || {}) };
      delete decisionsByReviewKey[candidate.reviewKey];
      this.caseSupportState.workflowState.addendumRecipientEntityReview = {
        ...existingReview,
        decisionsByReviewKey,
        lastTouchedUtc: new Date().toISOString()
      };
      const drafts = { ...(this.addendumRecipientDraftByKey || {}) };
      drafts[candidate.reviewKey] = '';
      this.addendumRecipientDraftByKey = drafts;
      const refreshed = this.refreshAddendumRecipientEntityReview({ silent: true });
      this.addendumRecipientStatus = `Cleared saved recipient choice for row ${candidate.sourceRowNumber || '—'}.`;
      if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
      return refreshed;
    },

    saveVisibleAddendumRecipientEntityChoices(options = {}) {
      const candidates = (this.addendumRecipientVisibleRows || []).filter(candidate => !candidate.isAutoResolved && this.getAddendumRecipientDraftValue(candidate) && normalizeCell(this.getAddendumRecipientDraftValue(candidate)).toLowerCase() !== normalizeCell(candidate.resolvedEntityName).toLowerCase());
      if (!candidates.length) {
        this.addendumRecipientStatus = 'No visible draft recipient choices need saving.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return this.getAddendumRecipientEntityReviewState();
      }
      let refreshed = null;
      candidates.forEach((candidate) => {
        refreshed = this.saveAddendumRecipientEntityChoice(candidate, this.getAddendumRecipientDraftValue(candidate), { silent: true, source: 'save-visible-addendum-recipients' }) || refreshed;
      });
      refreshed = refreshed || this.refreshAddendumRecipientEntityReview({ silent: true });
      this.addendumRecipientStatus = `Saved ${candidates.length} visible recipient choice${candidates.length === 1 ? '' : 's'}. ${refreshed.needsChoiceCount || 0} row${(refreshed.needsChoiceCount || 0) === 1 ? '' : 's'} still need a choice.`;
      if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
      return refreshed;
    },

    stageAddendumRecipientEntityRows(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging addendum recipient rows…',
          message: 'Building addendum review rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Addendum recipient rows staged.'
        }, () => this.stageAddendumRecipientEntityRows({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getAddendumRecipientEntityReviewState();
      if (!state.buildIsCurrent) {
        this.addendumRecipientStatus = state.summary || 'Review addendum path-ending rows before staging recipient-resolved rows.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return false;
      }
      if ((state.resolvedCount || 0) === 0) {
        this.addendumRecipientStatus = 'No addendum recipient rows are resolved yet.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return false;
      }
      this.loadOutputRows(buildCaseAddendumRecipientEntityOutputRows(state), {
        title: 'Addendum recipient-resolved rows',
        detail: `Tool48-style recipient resolution staged ${state.resolvedCount || 0} addendum row${(state.resolvedCount || 0) === 1 ? '' : 's'} in Output with Column K set to the resolved entity. Main Data is not modified.`,
        sourceActionId: 'resolve_addendum_recipient_entities',
        target: 'addendum-review-preview'
      });
      this.addendumRecipientStatus = `Staged ${state.resolvedCount || 0} recipient-resolved addendum row${(state.resolvedCount || 0) === 1 ? '' : 's'} in Output.`;
      if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
      this.scrollToSection('case-output');
      return true;
    },

    stageAddendumRecipientEntityAudit(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging addendum recipient audit…',
          message: 'Building addendum review rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Addendum recipient audit staged.'
        }, () => this.stageAddendumRecipientEntityAudit({ ...options, __caseOperationWrapped: true }));
      }
      const state = this.getAddendumRecipientEntityReviewState();
      this.loadOutputRows(buildCaseAddendumRecipientEntityAuditRows(state, options.actionLabel || 'Addendum recipient entity review audit'), {
        title: 'Addendum recipient entity review audit',
        detail: 'Audit-only review of auto-resolved and selected addendum recipient entities. Main Data is not modified.',
        sourceActionId: 'resolve_addendum_recipient_entities_audit',
        target: 'audit-only'
      });
      this.addendumRecipientStatus = `Addendum recipient audit staged. ${state.resolvedCount || 0}/${state.totalCandidateCount || 0} row${(state.totalCandidateCount || 0) === 1 ? '' : 's'} resolved.`;
      if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
      this.scrollToSection('case-output');
      return true;
    },

    downloadAddendumRecipientEntityTsv(options = {}) {
      const state = this.getAddendumRecipientEntityReviewState();
      if (!state.buildIsCurrent || (state.resolvedCount || 0) === 0) {
        this.addendumRecipientStatus = state.summary || 'No recipient-resolved addendum rows are ready to download.';
        if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
        return false;
      }
      downloadTextFile('addendum_recipient_resolved_rows.tsv', buildTsv(buildCaseAddendumRecipientEntityOutputRows(state)), 'text/tab-separated-values;charset=utf-8;');
      this.addendumRecipientStatus = 'Addendum recipient-resolved TSV downloaded.';
      if (!options.silent) this.showCaseToast(this.addendumRecipientStatus);
      return true;
    },

    markAddendumRecipientEntityReviewComplete(options = {}) {
      const state = this.getAddendumRecipientEntityReviewState();
      if (!state.buildIsCurrent) {
        this.addendumRecipientStatus = state.summary || 'Review addendum path-ending rows before confirming recipient entities.';
        this.showCaseToast(this.addendumRecipientStatus);
        return false;
      }
      if ((state.noCandidateEntityCount || 0) > 0) {
        this.addendumRecipientStatus = `${state.noCandidateEntityCount} addendum row${state.noCandidateEntityCount === 1 ? '' : 's'} have no candidate entity options.`;
        this.showCaseToast(this.addendumRecipientStatus);
        return false;
      }
      if ((state.needsChoiceCount || 0) > 0) {
        this.addendumRecipientStatus = `${state.needsChoiceCount} addendum recipient row${state.needsChoiceCount === 1 ? '' : 's'} still need a selected entity.`;
        this.showCaseToast(this.addendumRecipientStatus);
        return false;
      }
      const reviewedAtUtc = new Date().toISOString();
      const existingReview = this.caseSupportState.workflowState.addendumRecipientEntityReview && typeof this.caseSupportState.workflowState.addendumRecipientEntityReview === 'object'
        ? this.caseSupportState.workflowState.addendumRecipientEntityReview
        : {};
      this.caseSupportState.workflowState.addendumRecipientEntityReview = {
        ...existingReview,
        basisHash: state.currentReviewBasisHash,
        reviewedAtUtc,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        candidateCount: state.totalCandidateCount || 0,
        resolvedCount: state.resolvedCount || 0,
        autoResolvedCount: state.autoResolvedCount || 0,
        needsChoiceCount: state.needsChoiceCount || 0
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_ADDENDUM_RECIPIENTS_RESOLVED, options.source || 'resolve_addendum_recipient_entities', { silent: true });
      const refreshed = this.getAddendumRecipientEntityReviewState();
      this.updateAddendumRecipientEntityCheckResult(refreshed);
      this.stageAddendumRecipientEntityAudit({ actionLabel: 'Review confirmed', silent: true });
      this.addendumRecipientStatus = `Addendum recipient entity review marked current. ${refreshed.resolvedCount || 0}/${refreshed.totalCandidateCount || 0} row${(refreshed.totalCandidateCount || 0) === 1 ? '' : 's'} resolved.`;
      this.showCaseToast(this.addendumRecipientStatus);
      return true;
    },

    getCaseAddendumAddressLegendAnalysis() {
      const buildOptions = { labelDormantAddressesSeparately: this.addressLegendLabelDormantSeparately };
      const cacheKey = this.getCaseComputationCacheKey?.('addendum-address-legend-analysis', buildOptions) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseAddressLegendAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      )) || buildCaseAddressLegendAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.depositHotWalletEffectiveCurator,
        this.caseSupportState.workflowState || {},
        buildOptions
      );
    },

    getCaseAddendumVariantId(options = {}) {
      const requested = options.variantId || this.caseAddendumActiveVariant || CASE_ADDENDUM_VARIANT_STRUCTURED;
      return requested === CASE_ADDENDUM_VARIANT_NARRATIVE
        ? CASE_ADDENDUM_VARIANT_NARRATIVE
        : CASE_ADDENDUM_VARIANT_STRUCTURED;
    },

    getCaseAddendumState(options = {}) {
      const pathEndingState = options.pathEndingState || this.getAddendumPathEndingReviewState(options);
      const recipientState = options.recipientState || options.recipientEntityState || this.getAddendumRecipientEntityReviewState({
        ...(options || {}),
        pathEndingState
      });
      const addressLegendAnalysis = options.addressLegendAnalysis || options.legendAnalysis || this.getCaseAddendumAddressLegendAnalysis();
      const buildOptions = {
        caseId: this.currentProjectFileId || this.caseDisplayId || 'case',
        caseDataRevision: Number(this.caseSupportState.caseDataRevision) || 0,
        aggregateDuplicateHashes: this.caseAddendumAggregateDuplicateHashes === true,
        activePreviewVariantId: this.getCaseAddendumVariantId(options),
        ...(options || {}),
        addressLegendAnalysis: undefined,
        pathEndingState: undefined,
        recipientState: undefined,
        recipientEntityState: undefined,
        legendAnalysis: undefined
      };
      const cacheKey = this.getCaseComputationCacheKey?.('case-addendum-state', {
        ...buildOptions,
        addressLegendBasis: addressLegendAnalysis?.currentBasisHash || '',
        recipientBasis: recipientState?.currentBasisHash || recipientState?.currentReviewBasisHash || '',
        pathEndingBasis: pathEndingState?.currentBasisHash || ''
      }) || '';
      return this.getCachedCaseComputation?.('state', cacheKey, () => buildCaseAddendumState(recipientState, this.caseSupportState.workflowState || {}, {
        ...buildOptions,
        addressLegendAnalysis
      })) || buildCaseAddendumState(recipientState, this.caseSupportState.workflowState || {}, {
        ...buildOptions,
        addressLegendAnalysis
      });
    },

    getCaseAddendumVariantPayloadForActive(state = null, options = {}) {
      return buildCaseAddendumVariantPayload(state || this.getCaseAddendumState(options), this.getCaseAddendumVariantId(options));
    },

    async openCaseAddendumModal(options = {}) {
      return this.runCaseModalOperation({
        title: 'Opening addendum builder…',
        message: 'Building the current addendum preview.',
        target: 'case-addendum-modal',
        finishMessage: 'Addendum preview ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.showAddendumPathEndingReviewModal = false;
        this.showAddendumRecipientEntityModal = false;
        this.caseAddendumSearch = '';
        this.caseAddendumActiveVariant = this.getCaseAddendumVariantId(options);
        this.showCaseAddendumModal = true;
        await this.yieldToCaseUi();
        this.refreshCaseAddendumReview({ silent: true });
        return true;
      });
    },

    cancelCaseAddendumModal() {
      if (this.caseAddendumRefreshRunning) return;
      this.showCaseAddendumModal = false;
    },

    refreshCaseAddendumReview(options = {}) {
      if (this.caseAddendumRefreshRunning) return this.getCaseAddendumState(options);
      this.caseAddendumRefreshRunning = true;
      try {
        const state = this.getCaseAddendumState(options);
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          [CASE_ADDENDUM_BUILD_REVIEW_ID]: this.buildCaseAddendumCheckResult(state)
        };
        this.persistCaseSupportState();
        this.caseAddendumStatus = state.summary || 'Addendum review refreshed.';
        if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
        return state;
      } finally {
        this.caseAddendumRefreshRunning = false;
      }
    },

    buildCaseAddendumCheckResult(state = null) {
      const addendumState = state || this.getCaseAddendumState();
      if (!addendumState.prep55ReviewIsCurrent) {
        return this.buildCheckResult(CASE_ADDENDUM_BUILD_REVIEW_ID, 'fail', addendumState.summary || 'Review exchange path-ending rows before building the addendum.', addendumState);
      }
      if (!addendumState.prep48ReviewIsCurrent) {
        return this.buildCheckResult(CASE_ADDENDUM_BUILD_REVIEW_ID, 'fail', addendumState.summary || 'Resolve recipient entities for addendum rows before building the addendum.', addendumState);
      }
      if (!addendumState.canBuild) {
        return this.buildCheckResult(CASE_ADDENDUM_BUILD_REVIEW_ID, 'fail', addendumState.summary || 'Addendum prerequisites are not current.', addendumState);
      }
      if ((addendumState.resolvedRowCount || 0) === 0) {
        if (addendumState.reviewIsCurrent) {
          return this.buildCheckResult(CASE_ADDENDUM_BUILD_REVIEW_ID, 'pass', 'No addendum rows are needed for the current case revision; the no-addendum review is current.', addendumState);
        }
        return this.buildCheckResult(CASE_ADDENDUM_BUILD_REVIEW_ID, 'warn', 'Addendum prerequisites are current, but no resolved addendum rows are available to render. Mark reviewed to record that no addendum is needed.', addendumState);
      }
      if (addendumState.reviewIsCurrent) {
        return this.buildCheckResult(CASE_ADDENDUM_BUILD_REVIEW_ID, 'pass', `Addendum is built and reviewed for ${addendumState.resolvedRowCount || 0} row${(addendumState.resolvedRowCount || 0) === 1 ? '' : 's'} across ${addendumState.sectionCount || 0} recipient entit${(addendumState.sectionCount || 0) === 1 ? 'y' : 'ies'}.`, addendumState);
      }
      return this.buildCheckResult(CASE_ADDENDUM_BUILD_REVIEW_ID, 'warn', `Addendum is ready to review: ${addendumState.resolvedRowCount || 0} row${(addendumState.resolvedRowCount || 0) === 1 ? '' : 's'} across ${addendumState.sectionCount || 0} recipient entit${(addendumState.sectionCount || 0) === 1 ? 'y' : 'ies'}.`, addendumState);
    },

    updateCaseAddendumCheckResult(state = null) {
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        [CASE_ADDENDUM_BUILD_REVIEW_ID]: this.buildCaseAddendumCheckResult(state)
      };
      this.persistCaseSupportState();
    },

    markCaseAddendumDeliverableBuiltRecord(deliverableId = 'addendum', options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      const reviewedAtUtc = options.reviewedAtUtc || (options.keepReview ? this.caseSupportState.deliverableStateById?.[deliverableId]?.reviewedAtUtc : '');
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        [deliverableId]: {
          ...(this.caseSupportState.deliverableStateById?.[deliverableId] || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: options.builtAtUtc || new Date().toISOString(),
          basisHash: state.currentReviewBasisHash || '',
          reviewedAtUtc: reviewedAtUtc || ''
        }
      };
      this.updateCaseAddendumCheckResult(state);
      return state;
    },

    buildCaseAddendumPreviewRowsForVariant(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      return buildCaseAddendumPreviewRows(state, this.getCaseAddendumVariantId(options));
    },

    buildCaseAddendumAuditPreviewRows(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      return buildCaseAddendumAuditRows(state, options.actionLabel || 'Addendum review audit');
    },

    stageCaseAddendumPreview(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging addendum preview…',
          message: 'Building addendum review rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Addendum preview staged.'
        }, () => this.stageCaseAddendumPreview({ ...options, __caseOperationWrapped: true }));
      }
      const state = options.state || this.getCaseAddendumState(options);
      if (!state.canBuild) {
        this.caseAddendumStatus = state.summary || 'Addendum is not ready to stage.';
        if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
        return false;
      }
      const variantId = this.getCaseAddendumVariantId(options);
      const payload = buildCaseAddendumVariantPayload(state, variantId);
      this.loadOutputRows(buildCaseAddendumPreviewRows(state, variantId), {
        title: `${payload.displayName} preview`,
        detail: 'Addendum preview staged in Output/Review. Main Data is not modified.',
        sourceActionId: 'build_case_addendum',
        sourceDeliverableId: payload.deliverableId,
        target: 'addendum-review-preview'
      });
      this.markCaseAddendumDeliverableBuiltRecord(payload.deliverableId, { state, keepReview: options.keepReview });
      this.caseAddendumStatus = `${payload.displayName} staged in Output with ${state.resolvedRowCount || 0} rendered row${(state.resolvedRowCount || 0) === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
      this.scrollToSection('case-output');
      return true;
    },

    stageCaseAddendumAudit(options = {}) {
      if (this.shouldRunCaseOperationWrapper?.(options)) {
        return this.runCaseModalOperation({
          title: 'Staging addendum audit…',
          message: 'Building addendum review rows and updating Output/Review.',
          stepMessage: 'Preparing current case data…',
          target: 'deliverable-preview',
          finishMessage: 'Addendum audit staged.'
        }, () => this.stageCaseAddendumAudit({ ...options, __caseOperationWrapped: true }));
      }
      const state = options.state || this.getCaseAddendumState(options);
      this.loadOutputRows(buildCaseAddendumAuditRows(state, options.actionLabel || 'Addendum review audit'), {
        title: 'Addendum review audit',
        detail: 'Audit-only view of the addendum build state. Main Data is not modified.',
        sourceActionId: 'build_case_addendum_audit',
        sourceDeliverableId: 'addendum_audit',
        target: 'audit-only'
      });
      this.markCaseAddendumDeliverableBuiltRecord('addendum_audit', { state, keepReview: options.keepReview });
      this.caseAddendumStatus = `Addendum audit staged for ${state.resolvedRowCount || 0} rendered row${(state.resolvedRowCount || 0) === 1 ? '' : 's'}.`;
      if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
      this.scrollToSection('case-output');
      return true;
    },

    downloadCaseAddendumText(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      if (!state.canBuild) {
        this.caseAddendumStatus = state.summary || 'Addendum is not ready to download.';
        if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
        return false;
      }
      const variantId = this.getCaseAddendumVariantId(options);
      const payload = buildCaseAddendumVariantPayload(state, variantId);
      downloadTextFile(`${payload.filenameStem || 'addendum'}.txt`, payload.plainText || '', 'text/plain;charset=utf-8;');
      this.markCaseAddendumDeliverableBuiltRecord(payload.deliverableId, { state, keepReview: true });
      this.caseAddendumStatus = `${payload.displayName} text downloaded.`;
      if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
      return true;
    },

    downloadCaseAddendumHtml(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      if (!state.canBuild) {
        this.caseAddendumStatus = state.summary || 'Addendum is not ready to download.';
        if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
        return false;
      }
      const variantId = this.getCaseAddendumVariantId(options);
      const payload = buildCaseAddendumVariantPayload(state, variantId);
      downloadTextFile(`${payload.filenameStem || 'addendum'}.html`, payload.htmlDocument || payload.htmlFragment || '', 'text/html;charset=utf-8;');
      this.markCaseAddendumDeliverableBuiltRecord(payload.deliverableId, { state, keepReview: true });
      this.caseAddendumStatus = `${payload.displayName} HTML downloaded.`;
      if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
      return true;
    },

    downloadCaseAddendumAuditTsv(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      downloadTextFile('addendum_audit.tsv', buildTsv(buildCaseAddendumAuditRows(state, options.actionLabel || 'Addendum audit download')), 'text/tab-separated-values;charset=utf-8;');
      this.markCaseAddendumDeliverableBuiltRecord('addendum_audit', { state, keepReview: true });
      this.caseAddendumStatus = 'Addendum audit TSV downloaded.';
      if (!options.silent) this.showCaseToast(this.caseAddendumStatus);
      return true;
    },

    async copyCaseAddendumText(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      if (!state.canBuild) {
        this.caseAddendumStatus = state.summary || 'Addendum is not ready to copy.';
        this.showCaseToast(this.caseAddendumStatus);
        return false;
      }
      const variantId = this.getCaseAddendumVariantId(options);
      const payload = buildCaseAddendumVariantPayload(state, variantId);
      const copied = await this.copyTextToClipboard(payload.plainText || '', `${payload.displayName} text copied.`);
      if (copied) this.markCaseAddendumDeliverableBuiltRecord(payload.deliverableId, { state, keepReview: true });
      this.caseAddendumStatus = copied ? `${payload.displayName} text copied.` : 'Could not copy addendum text in this browser.';
      return copied;
    },

    async copyCaseAddendumRich(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      if (!state.canBuild) {
        this.caseAddendumStatus = state.summary || 'Addendum is not ready to copy.';
        this.showCaseToast(this.caseAddendumStatus);
        return false;
      }
      const variantId = this.getCaseAddendumVariantId(options);
      const payload = buildCaseAddendumVariantPayload(state, variantId);
      try {
        if (typeof navigator !== 'undefined' && navigator?.clipboard?.write && typeof ClipboardItem !== 'undefined') {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([payload.htmlDocument || payload.htmlFragment || ''], { type: 'text/html' }),
              'text/plain': new Blob([payload.plainText || ''], { type: 'text/plain' })
            })
          ]);
          this.markCaseAddendumDeliverableBuiltRecord(payload.deliverableId, { state, keepReview: true });
          this.caseAddendumStatus = `${payload.displayName} rich output copied to clipboard.`;
          this.showCaseToast(this.caseAddendumStatus);
          return true;
        }
      } catch {
        // Fall back to text-only copy below.
      }
      const copied = await this.copyTextToClipboard(payload.plainText || '', `${payload.displayName} text copied because rich clipboard was unavailable.`);
      if (copied) this.markCaseAddendumDeliverableBuiltRecord(payload.deliverableId, { state, keepReview: true });
      this.caseAddendumStatus = copied ? 'Rich clipboard unavailable; copied text-only addendum instead.' : 'Could not copy addendum in this browser.';
      return copied;
    },

    markCaseAddendumReviewComplete(options = {}) {
      const state = options.state || this.getCaseAddendumState(options);
      if (!state.canBuild) {
        this.caseAddendumStatus = state.summary || 'Resolve addendum prerequisites before marking the addendum reviewed.';
        this.showCaseToast(this.caseAddendumStatus);
        return false;
      }
      const noAddendumRows = (state.resolvedRowCount || 0) === 0;
      const reviewedAtUtc = new Date().toISOString();
      this.caseSupportState.workflowState.addendumBuildReview = {
        ...(this.caseSupportState.workflowState.addendumBuildReview || {}),
        basisHash: state.currentReviewBasisHash,
        confirmedAtUtc: reviewedAtUtc,
        reviewedAtUtc,
        revision: Number(this.caseSupportState.caseDataRevision) || 0,
        rowCount: state.resolvedRowCount || 0,
        rawResolvedRowCount: state.rawResolvedRowCount || 0,
        sectionCount: state.sectionCount || 0,
        duplicateGroupCount: state.duplicateGroupCount || 0,
        condensedOmittedRowCount: state.condensedOmittedRowCount || 0,
        aggregateDuplicateHashes: !!state.aggregateDuplicateHashes,
        rendererVersion: state.rendererVersion || CASE_ADDENDUM_RENDERER_VERSION
      };
      const refreshed = this.getCaseAddendumState(options);
      this.caseSupportState.deliverableStateById = {
        ...(this.caseSupportState.deliverableStateById || {}),
        addendum: {
          ...(this.caseSupportState.deliverableStateById?.addendum || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.addendum?.builtAtUtc || reviewedAtUtc,
          basisHash: refreshed.currentReviewBasisHash || '',
          reviewedAtUtc
        },
        addendum_narrative: {
          ...(this.caseSupportState.deliverableStateById?.addendum_narrative || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.addendum_narrative?.builtAtUtc || reviewedAtUtc,
          basisHash: refreshed.currentReviewBasisHash || '',
          reviewedAtUtc
        },
        addendum_audit: {
          ...(this.caseSupportState.deliverableStateById?.addendum_audit || {}),
          builtRevision: Number(this.caseSupportState.caseDataRevision) || 0,
          builtAtUtc: this.caseSupportState.deliverableStateById?.addendum_audit?.builtAtUtc || reviewedAtUtc,
          basisHash: refreshed.currentReviewBasisHash || '',
          reviewedAtUtc
        }
      };
      this.recordWorkflowSignal(WORKFLOW_SIGNAL_ADDENDUM_REVIEWED, options.source || 'review_case_addendum', { silent: true });
      this.updateCaseAddendumCheckResult(refreshed);
      this.stageCaseAddendumAudit({ state: refreshed, actionLabel: noAddendumRows ? 'No-addendum review confirmed' : 'Addendum reviewed', keepReview: true, silent: true });
      this.caseAddendumStatus = noAddendumRows
        ? 'No addendum rows are needed for this case revision. Addendum step marked reviewed.'
        : `Addendum marked reviewed with ${refreshed.resolvedRowCount || 0} rendered row${(refreshed.resolvedRowCount || 0) === 1 ? '' : 's'} across ${refreshed.sectionCount || 0} recipient entit${(refreshed.sectionCount || 0) === 1 ? 'y' : 'ies'}.`;
      this.showCaseToast(this.caseAddendumStatus);
      return true;
    },

    toggleCaseAddendumReviewed(item = {}) {
      const id = item?.id === 'addendum_narrative' || item?.generator === 'case_addendum_narrative'
        ? 'addendum_narrative'
        : item?.id === 'addendum_audit' || item?.generator === 'case_addendum_audit'
          ? 'addendum_audit'
          : 'addendum';
      const existing = this.caseSupportState.deliverableStateById?.[id] || {};
      const state = this.getCaseAddendumState();
      const currentRevision = Number(this.caseSupportState.caseDataRevision) || 0;
      const builtIsCurrent = !!existing.builtAtUtc && Number(existing.builtRevision) === currentRevision && existing.basisHash === state.currentReviewBasisHash;
      if (!builtIsCurrent) {
        this.showCaseToast('Stage, copy, or download the current addendum before marking it reviewed.');
        return false;
      }
      if (existing.reviewedAtUtc) {
        this.caseSupportState.deliverableStateById = {
          ...(this.caseSupportState.deliverableStateById || {}),
          [id]: { ...existing, reviewedAtUtc: '' }
        };
        this.updateCaseAddendumCheckResult(state);
        this.showCaseToast('Addendum review mark cleared.');
        return true;
      }
      return this.markCaseAddendumReviewComplete({ source: 'deliverables-panel' });
    },

};
